import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type {
  Pricing,
  RentedSubscription,
  Subscription,
  SubscriptionType,
} from "common/types";

import { post } from "../helpers/HTTP";

async function workspaceEstimateCreationRequest(
  workspaceId: string,
  planId: string,
  addonId: string,
  quantity: number,
  type: SubscriptionType,
): Promise<{ amountDue: number; nextBillingAt: number; pricePerItem: number }> {
  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/estimates/create`,
    {
      type,
      items: [
        {
          id: planId,
        },
        ...(quantity > 0
          ? [
              {
                id: addonId,
                quantity,
              },
            ]
          : []),
      ],
    },
  );

  const {
    invoice_estimate: invoiceEstimate,
    subscription_estimate: subscriptionEstimate,
    next_invoice_estimate: nextInvoiceEstimate,
  } = estimate;

  return {
    amountDue: invoiceEstimate
      ? invoiceEstimate.amount_due
      : nextInvoiceEstimate.amount_due,
    nextBillingAt: subscriptionEstimate.next_billing_at,
    pricePerItem: invoiceEstimate.line_items.find(
      ({ pricing_model }: { pricing_model: string }) =>
        pricing_model === "per_unit",
    ).unit_amount,
  };
}

export default function useWorkspaceEstimateCreation({
  subscription,
  pricing,
  quantity,
}: {
  subscription?: Subscription | RentedSubscription;
  pricing: { addon: Pricing; plan: Pricing };
  quantity: number;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { addon, plan } = pricing;

  const {
    data: estimateCreation,
    isFetching: isLoadingEstimateCreation,
    error: estimateCreationError,
  } = useQuery({
    queryKey: [
      "estimateCreation",
      workspaceId,
      subscription ? subscription.id : "no subscription",
      pricing,
      quantity,
    ],
    queryFn: () =>
      workspaceEstimateCreationRequest(
        workspaceId,
        plan.id,
        addon.id,
        quantity,
        pricing.plan.subscription_type,
      ),
    enabled: !subscription || subscription.status === "non_renewing",
  });

  return { estimateCreation, isLoadingEstimateCreation, estimateCreationError };
}
